import type { OrganizationSettings } from '@/contexts/OrganizationContext';

/**
 * Default organization settings used when no custom settings are configured
 */
export const defaultSettings: OrganizationSettings = {
  site_title: 'Build With Christ',
  site_description: 'AI for human flourishing',
  favicon_url: null,
  primary_color: '199 89% 32%',
  secondary_color: '160 84% 39%',
  background_color: '222 47% 11%',
  foreground_color: '210 40% 98%',
  font_heading: 'Space Grotesk',
  font_body: 'Inter',
  enable_sandbox: true,
  enable_library: true,
  enable_events: true,
  enable_ai_chat: true,
  enable_integrations: false,
  require_auth: false,
  library_requires_auth: false,
  planner_requires_auth: false,
  contact_email: null,
  planner_label: 'Planner',
  planner_system_prompt: null,
  planner_mode: 'chat',
  planner_welcome_subheader: null,
  planner_welcome_example: null,
  planner_wizard_config: null,
  ai_system_instructions: null,
  ai_context_provider: 'lovable',
};

/**
 * Maps raw settings data from the database to the OrganizationSettings interface.
 * This eliminates the duplicate mapping logic that was in OrganizationContext.
 * 
 * @param settingsData - Raw settings data from get_public_organization_settings RPC
 * @param defaults - Default settings to use for missing values
 * @returns Properly typed OrganizationSettings object
 */
export function mapSettingsToOrganizationSettings(
  settingsData: Record<string, unknown> | null,
  defaults: OrganizationSettings = defaultSettings
): OrganizationSettings {
  if (!settingsData) return defaults;

  return {
    site_title: (settingsData.site_title as string | null) ?? defaults.site_title,
    site_description: (settingsData.site_description as string | null) ?? defaults.site_description,
    favicon_url: (settingsData.favicon_url as string | null) ?? defaults.favicon_url,
    primary_color: (settingsData.primary_color as string) ?? defaults.primary_color,
    secondary_color: (settingsData.secondary_color as string) ?? defaults.secondary_color,
    background_color: (settingsData.background_color as string) ?? defaults.background_color,
    foreground_color: (settingsData.foreground_color as string) ?? defaults.foreground_color,
    font_heading: (settingsData.font_heading as string) ?? defaults.font_heading,
    font_body: (settingsData.font_body as string) ?? defaults.font_body,
    enable_sandbox: (settingsData.enable_sandbox as boolean) ?? defaults.enable_sandbox,
    enable_library: (settingsData.enable_library as boolean) ?? defaults.enable_library,
    enable_events: (settingsData.enable_events as boolean) ?? defaults.enable_events,
    enable_ai_chat: (settingsData.enable_ai_chat as boolean) ?? defaults.enable_ai_chat,
    enable_integrations: (settingsData.enable_integrations as boolean) ?? defaults.enable_integrations,
    require_auth: (settingsData.require_auth as boolean) ?? defaults.require_auth,
    library_requires_auth: (settingsData.library_requires_auth as boolean) ?? defaults.library_requires_auth,
    planner_requires_auth: (settingsData.planner_requires_auth as boolean) ?? defaults.planner_requires_auth,
    contact_email: (settingsData.contact_email as string | null) ?? defaults.contact_email,
    planner_label: (settingsData.planner_label as string | null) ?? defaults.planner_label,
    planner_system_prompt: (settingsData.planner_system_prompt as string | null) ?? defaults.planner_system_prompt,
    planner_mode: ((settingsData.planner_mode as 'chat' | 'wizard') ?? defaults.planner_mode),
    planner_welcome_subheader: (settingsData.planner_welcome_subheader as string | null) ?? defaults.planner_welcome_subheader,
    planner_welcome_example: (settingsData.planner_welcome_example as string | null) ?? defaults.planner_welcome_example,
    planner_wizard_config: (settingsData.planner_wizard_config as Record<string, unknown> | null) ?? defaults.planner_wizard_config,
    ai_system_instructions: (settingsData.ai_system_instructions as string | null) ?? defaults.ai_system_instructions,
    ai_context_provider: ((settingsData.ai_context_provider as 'none' | 'lovable' | 'magisterium') ?? defaults.ai_context_provider),
  };
}
